import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Skeleton
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale'
import { useAuth } from '../../../../auth/useAuth'
import { useTableSort } from '../../../../hooks/useTableSort'
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader'

// Types
interface Position {
  Position_Id: number
  Position_Fund: string
  Position_Issuer: string
  Position_Name: string
  Position_Industry: string
  Position_Geography: string
  Position_Collateral: string
  Position_Rating: string
  Position_Position_Type: string
  Position_Lien: string
  Position_Ownership: string
  Position_Target_IRR: number | null
  Position_Realized_IRR: number | null
  Position_Total_Yield: number | null
  Position_Cash_Yield: number | null
  Position_PIK: number | null
  Position_Fixed_Floating: string
  Position_Fees_Upfront: number | null
  Position_Date_Invested: Date | null
  Position_Min_IRR_Guarantee: number | null
  Position_Maturity: Date | null
  Position_Total_Position_Size: number | null
  Position_Amount_Committed: number | null
  Position_Amount_Invested: number | null
  Position_Currency: string
  Position_Annual_Ammortization: number | null
  Position_Opening_LTV: number | null
  Position_Opening_DEBT_EBITDA: number | null
  Position_Date_Realized: Date | null
  Position_Description_Loan: string
  Position_Description_LTV: string
  Position_Asof: Date | null
  Position_Timestamp: Date
  Position_Deleted: boolean
  CreatedBy?: string
  ModifiedBy?: string
}

interface PaginationInfo {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

interface PaginatedResponse {
  data: Position[]
  pagination: PaginationInfo
}

const initialPosition: Omit<Position, 'Position_Id' | 'Position_Timestamp'> = {
  Position_Fund: '',
  Position_Issuer: '',
  Position_Name: '',
  Position_Industry: '',
  Position_Geography: '',
  Position_Collateral: '',
  Position_Rating: '',
  Position_Position_Type: '',
  Position_Lien: '',
  Position_Ownership: '',
  Position_Target_IRR: null,
  Position_Realized_IRR: null,
  Position_Total_Yield: null,
  Position_Cash_Yield: null,
  Position_PIK: null,
  Position_Fixed_Floating: '',
  Position_Fees_Upfront: null,
  Position_Date_Invested: null,
  Position_Min_IRR_Guarantee: null,
  Position_Maturity: null,
  Position_Total_Position_Size: null,
  Position_Amount_Committed: null,
  Position_Amount_Invested: null,
  Position_Currency: '',
  Position_Annual_Ammortization: null,
  Position_Opening_LTV: null,
  Position_Opening_DEBT_EBITDA: null,
  Position_Date_Realized: null,
  Position_Description_Loan: '',
  Position_Description_LTV: '',
  Position_Asof: null,
  Position_Deleted: false
}

const PositionsManagement: React.FC = () => {
  const { user, getAccessToken } = useAuth()
  const [positions, setPositions] = useState<Position[]>([])
  const [funds, setFunds] = useState<string[]>([])
  const [industries, setIndustries] = useState<string[]>([])
  const [geographies, setGeographies] = useState<string[]>([])
  const [positionTypes, setPositionTypes] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [backgroundLoading, setBackgroundLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // Pagination state
  const [totalCount, setTotalCount] = useState(0)
  const [loadedCount, setLoadedCount] = useState(0)
  const [currentBackendPage, setCurrentBackendPage] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  
  // Form state
  const [formData, setFormData] = useState<Omit<Position, 'Position_Id' | 'Position_Timestamp'>>(initialPosition)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [appliedFilters, setAppliedFilters] = useState<Omit<Position, 'Position_Id' | 'Position_Timestamp'>>(initialPosition)
  
  // Table state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedFund, setSelectedFund] = useState('')
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [positionToDelete, setPositionToDelete] = useState<Position | null>(null)

  // Identifier state
  const IDENTIFIER_TYPES = ['BEZ', 'CUSIP', 'ISIN', 'LEI']
  const [positionIdType, setPositionIdType] = useState<string>('')
  const [positionIdValue, setPositionIdValue] = useState<string>('')
  const [positionIssuerIdType, setPositionIssuerIdType] = useState<string>('')
  const [positionIssuerIdValue, setPositionIssuerIdValue] = useState<string>('')

  // Dropdown options (static)
  const ratings = ['AAA', 'AA+', 'AA', 'AA-', 'A+', 'A', 'A-', 'BBB+', 'BBB', 'BBB-', 'BB+', 'BB', 'BB-', 'B+', 'B', 'B-', 'CCC+', 'CCC', 'CCC-', 'CC', 'C', 'D']
  const lienTypes = ['First Lien', 'Second Lien', 'Third Lien', 'Unsecured']
  const currencies = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD']
  const fixedFloating = ['Fixed', 'Floating', 'Mixed']

  // Load initial positions data with pagination
  const loadInitialPositions = async () => {
    setLoading(true)
    setError(null)
    setIsInitialLoad(true)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      // First get total count
      const countResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/positions/?count_only=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!countResponse.ok) {
        throw new Error('Failed to get positions count')
      }
      
      const countData = await countResponse.json()
      setTotalCount(countData.total_count)
      
      // Then load first page (100 records)
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/positions/?page=1&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to load positions')
      }
      
      const data: PaginatedResponse = await response.json()
      
      // Convert date strings to Date objects
      const processedPositions = data.data.map(position => ({
        ...position,
        Position_Date_Invested: position.Position_Date_Invested ? new Date(position.Position_Date_Invested) : null,
        Position_Maturity: position.Position_Maturity ? new Date(position.Position_Maturity) : null,
        Position_Date_Realized: position.Position_Date_Realized ? new Date(position.Position_Date_Realized) : null,
        Position_Asof: position.Position_Asof ? new Date(position.Position_Asof) : null,
        Position_Timestamp: new Date(position.Position_Timestamp)
      }))
      
      setPositions(processedPositions)
      setLoadedCount(processedPositions.length)
      setCurrentBackendPage(1)
      setHasMoreData(data.pagination.has_next)
      
      // Start background loading if there's more data
      if (data.pagination.has_next && countData.total_count > 100) {
        setTimeout(() => loadMorePositionsInBackground(countData.total_count), 1000)
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load positions')
      // For development, show sample data
      const samplePositions = [
        {
          Position_Id: 1,
          Position_Fund: 'Sample Fund I',
          Position_Issuer: 'Sample Capital',
          Position_Name: 'Tech Company A',
          Position_Industry: 'Technology',
          Position_Geography: 'North America',
          Position_Collateral: 'IP and Assets',
          Position_Rating: 'BB+',
          Position_Position_Type: 'Senior Debt',
          Position_Lien: 'First Lien',
          Position_Ownership: '15%',
          Position_Target_IRR: 12.5,
          Position_Realized_IRR: 14.2,
          Position_Total_Yield: 8.5,
          Position_Cash_Yield: 6.0,
          Position_PIK: 2.5,
          Position_Fixed_Floating: 'Floating',
          Position_Fees_Upfront: 1.5,
          Position_Date_Invested: new Date('2023-06-15'),
          Position_Min_IRR_Guarantee: 8.0,
          Position_Maturity: new Date('2028-06-15'),
          Position_Total_Position_Size: 50000000,
          Position_Amount_Committed: 25000000,
          Position_Amount_Invested: 20000000,
          Position_Currency: 'USD',
          Position_Annual_Ammortization: 5000000,
          Position_Opening_LTV: 65.0,
          Position_Opening_DEBT_EBITDA: 4.2,
          Position_Date_Realized: null,
          Position_Description_Loan: 'Senior secured term loan facility for growth capital and working capital needs.',
          Position_Description_LTV: 'LTV calculated based on enterprise value at time of investment with quarterly revaluation.',
          Position_Asof: new Date(),
          Position_Timestamp: new Date(),
          Position_Deleted: false,
          CreatedBy: user?.username || 'system',
          ModifiedBy: user?.username || 'system'
        }
      ]
      setPositions(samplePositions)
      setTotalCount(1)
      setLoadedCount(1)
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }

  // Load all remaining positions in background
  const loadMorePositionsInBackground = async (totalRecords: number) => {
    if (!hasMoreData || backgroundLoading) return
    
    setBackgroundLoading(true)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        return
      }
      
      // Calculate how many records remain
      const remainingRecords = totalRecords - loadedCount
      if (remainingRecords <= 0) {
        setHasMoreData(false)
        return
      }
      
      // Load all remaining records in one request using page=1 with a large limit
      // This ensures we get all records from the beginning, then we'll replace the dataset
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/positions/?page=1&limit=${totalRecords}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        return
      }
      
      const data: PaginatedResponse = await response.json()
      
      // Convert date strings to Date objects
      const processedPositions = data.data.map(position => ({
        ...position,
        Position_Date_Invested: position.Position_Date_Invested ? new Date(position.Position_Date_Invested) : null,
        Position_Maturity: position.Position_Maturity ? new Date(position.Position_Maturity) : null,
        Position_Date_Realized: position.Position_Date_Realized ? new Date(position.Position_Date_Realized) : null,
        Position_Asof: position.Position_Asof ? new Date(position.Position_Asof) : null,
        Position_Timestamp: new Date(position.Position_Timestamp)
      }))
      
      // Replace all positions with the complete dataset
      setPositions(processedPositions)
      setLoadedCount(processedPositions.length)
      setHasMoreData(false) // All data loaded
      
    } catch (err) {
      // Silently fail background loading
      console.warn('Background loading failed:', err)
    } finally {
      setBackgroundLoading(false)
    }
  }

  // Refresh all data
  const refreshPositions = () => {
    setPositions([])
    setTotalCount(0)
    setLoadedCount(0)
    setCurrentBackendPage(1)
    setHasMoreData(true)
    loadInitialPositions()
  }

  // Load funds for dropdown - using the optimized distinct fund names endpoint
  const loadFunds = async () => {
    try {
      const token = await getAccessToken()
      
      if (!token) {
        // Only fall back to dummy data when no token (development mode)
        setFunds(['Sample Fund I', 'Sample Fund II', 'Growth Fund III'])
        return
      }
      
      // Use the new optimized endpoint for distinct fund names
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/funds/names`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const fundNames = await response.json()
        // The API already returns distinct, sorted fund names
        setFunds(fundNames)
      } else {
        console.error('Failed to load fund names from API:', response.status, response.statusText)
        // Only set empty array, don't fall back to dummy data
        setFunds([])
      }
    } catch (err) {
      console.error('Error loading fund names:', err)
      // Only set empty array, don't fall back to dummy data
      setFunds([])
    }
  }

  // Load industries from database and combine with local position data
  const loadIndustries = async () => {
    try {
      const token = await getAccessToken()
      
      if (!token) {
        setIndustries(['Technology', 'Healthcare', 'Financial Services', 'Real Estate', 'Energy', 'Consumer', 'Industrial', 'Infrastructure'])
        return
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/positions/industries`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const dbIndustries = await response.json()
        const localIndustries = [...new Set(positions.map(pos => pos.Position_Industry).filter(ind => ind && ind.trim()))]
        const defaultIndustries = ['Technology', 'Healthcare', 'Financial Services', 'Real Estate', 'Energy', 'Consumer', 'Industrial', 'Infrastructure']
        const allIndustries = [...new Set([...dbIndustries, ...localIndustries, ...defaultIndustries])]
        allIndustries.sort()
        setIndustries(allIndustries)
      } else {
        setIndustries(['Technology', 'Healthcare', 'Financial Services', 'Real Estate', 'Energy', 'Consumer', 'Industrial', 'Infrastructure'])
      }
    } catch (err) {
      console.error('Error loading industries:', err)
      setIndustries(['Technology', 'Healthcare', 'Financial Services', 'Real Estate', 'Energy', 'Consumer', 'Industrial', 'Infrastructure'])
    }
  }

  // Load geographies from database and combine with local position data
  const loadGeographies = async () => {
    try {
      const token = await getAccessToken()
      
      if (!token) {
        setGeographies(['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa'])
        return
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/positions/geographies`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const dbGeographies = await response.json()
        const localGeographies = [...new Set(positions.map(pos => pos.Position_Geography).filter(geo => geo && geo.trim()))]
        const defaultGeographies = ['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa']
        const allGeographies = [...new Set([...dbGeographies, ...localGeographies, ...defaultGeographies])]
        allGeographies.sort()
        setGeographies(allGeographies)
      } else {
        setGeographies(['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa'])
      }
    } catch (err) {
      console.error('Error loading geographies:', err)
      setGeographies(['North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Africa'])
    }
  }

  // Load position types from database and combine with local position data
  const loadPositionTypes = async () => {
    try {
      const token = await getAccessToken()
      
      if (!token) {
        setPositionTypes(['Senior Debt', 'Subordinated Debt', 'Mezzanine', 'Equity', 'Preferred Equity', 'Convertible', 'Warrant'])
        return
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/positions/types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const dbPositionTypes = await response.json()
        const localPositionTypes = [...new Set(positions.map(pos => pos.Position_Position_Type).filter(type => type && type.trim()))]
        const defaultPositionTypes = ['Senior Debt', 'Subordinated Debt', 'Mezzanine', 'Equity', 'Preferred Equity', 'Convertible', 'Warrant']
        const allPositionTypes = [...new Set([...dbPositionTypes, ...localPositionTypes, ...defaultPositionTypes])]
        allPositionTypes.sort()
        setPositionTypes(allPositionTypes)
      } else {
        setPositionTypes(['Senior Debt', 'Subordinated Debt', 'Mezzanine', 'Equity', 'Preferred Equity', 'Convertible', 'Warrant'])
      }
    } catch (err) {
      console.error('Error loading position types:', err)
      setPositionTypes(['Senior Debt', 'Subordinated Debt', 'Mezzanine', 'Equity', 'Preferred Equity', 'Convertible', 'Warrant'])
    }
  }

  useEffect(() => {
    loadInitialPositions()
    loadFunds()
    loadIndustries()
    loadGeographies()
    loadPositionTypes()
  }, [])

  // Update dropdown data when positions data changes
  useEffect(() => {
    if (positions.length > 0) {
      loadIndustries()
      loadGeographies()
      loadPositionTypes()
    }
  }, [positions])

  // Handle form changes
  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Calculate derived metrics
  const calculateMetrics = () => {
    const { Position_Amount_Invested, Position_Total_Position_Size, Position_Cash_Yield, Position_PIK } = formData
    
    if (Position_Amount_Invested && Position_Total_Position_Size) {
      const ownership = (Position_Amount_Invested / Position_Total_Position_Size) * 100
      handleInputChange('Position_Ownership', `${ownership.toFixed(2)}%`)
    }
    
    if (Position_Cash_Yield && Position_PIK) {
      const totalYield = Position_Cash_Yield + Position_PIK
      handleInputChange('Position_Total_Yield', totalYield)
    }
  }

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    setSuccess(null)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      const url = editingId 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/positions/${editingId}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/positions/`
      
      const method = editingId ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          CreatedBy: editingId ? undefined : user?.username || 'system',
          ModifiedBy: user?.username || 'system'
        })
      })
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingId ? 'update' : 'create'} position`)
      }
      
      const result = await response.json()
      const updatedPosition = result.position
      
      if (editingId) {
        // Update existing position in the list
        setPositions(prevPositions => prevPositions.map(position => 
          position.Position_Id === editingId ? {
            ...updatedPosition,
            Position_Date_Invested: updatedPosition.Position_Date_Invested ? new Date(updatedPosition.Position_Date_Invested) : null,
            Position_Maturity: updatedPosition.Position_Maturity ? new Date(updatedPosition.Position_Maturity) : null,
            Position_Date_Realized: updatedPosition.Position_Date_Realized ? new Date(updatedPosition.Position_Date_Realized) : null,
            Position_Asof: updatedPosition.Position_Asof ? new Date(updatedPosition.Position_Asof) : null,
            Position_Timestamp: new Date(updatedPosition.Position_Timestamp)
          } : position
        ))
      } else {
        // Add new position to the list
        const newPosition = {
          ...updatedPosition,
          Position_Date_Invested: updatedPosition.Position_Date_Invested ? new Date(updatedPosition.Position_Date_Invested) : null,
          Position_Maturity: updatedPosition.Position_Maturity ? new Date(updatedPosition.Position_Maturity) : null,
          Position_Date_Realized: updatedPosition.Position_Date_Realized ? new Date(updatedPosition.Position_Date_Realized) : null,
          Position_Asof: updatedPosition.Position_Asof ? new Date(updatedPosition.Position_Asof) : null,
          Position_Timestamp: new Date(updatedPosition.Position_Timestamp)
        }
        setPositions(prevPositions => [newPosition, ...prevPositions])
        setTotalCount(prevCount => prevCount + 1)
        setLoadedCount(prevCount => prevCount + 1)
      }
      
      // Save identifiers if any are filled in
      const identifiersToSave: Array<{entityType: string, idType: string, idValue: string}> = []
      if (positionIdType && positionIdValue) {
        identifiersToSave.push({ entityType: 'PositionID', idType: positionIdType, idValue: positionIdValue })
      }
      if (positionIssuerIdType && positionIssuerIdValue) {
        identifiersToSave.push({ entityType: 'PositionIssuerID', idType: positionIssuerIdType, idValue: positionIssuerIdValue })
      }

      const identifierWarnings: string[] = []
      for (const id of identifiersToSave) {
        try {
          const idResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              Entity_Type: id.entityType,
              Entity_Name: formData.Position_Name,
              Identifier_Type: id.idType,
              Identifier_Value: id.idValue,
              Asof: formData.Position_Asof ? (formData.Position_Asof instanceof Date ? formData.Position_Asof.toISOString() : formData.Position_Asof) : null,
              Deleted: formData.Position_Deleted ? 1 : 0
            })
          })
          if (!idResponse.ok) {
            const errData = await idResponse.json().catch(() => ({}))
            identifierWarnings.push(errData.detail || `Failed to save ${id.entityType} identifier`)
          }
        } catch (idErr) {
          identifierWarnings.push(`Failed to save ${id.entityType} identifier`)
        }
      }

      const baseMsg = `Position ${editingId ? 'updated' : 'created'} successfully`
      if (identifierWarnings.length > 0) {
        setSuccess(baseMsg)
        setError(`Identifier warnings: ${identifierWarnings.join('; ')}`)
      } else {
        setSuccess(baseMsg + (identifiersToSave.length > 0 ? ` with ${identifiersToSave.length} identifier(s)` : ''))
      }
      handleClear()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    } finally {
      setLoading(false)
    }
  }

  // Handle edit
  const handleEdit = async (position: Position) => {
    try {
      // Convert date strings to Date objects if they're strings
      const parseDate = (dateValue: any): Date | null => {
        if (!dateValue) return null
        if (dateValue instanceof Date) return dateValue
        if (typeof dateValue === 'string') {
          const parsed = new Date(dateValue)
          return isNaN(parsed.getTime()) ? null : parsed
        }
        return null
      }

      setFormData({
        Position_Fund: position.Position_Fund || '',
        Position_Issuer: position.Position_Issuer || '',
        Position_Name: position.Position_Name || '',
        Position_Industry: position.Position_Industry || '',
        Position_Geography: position.Position_Geography || '',
        Position_Collateral: position.Position_Collateral || '',
        Position_Rating: position.Position_Rating || '',
        Position_Position_Type: position.Position_Position_Type || '',
        Position_Lien: position.Position_Lien || '',
        Position_Ownership: position.Position_Ownership || '',
        Position_Target_IRR: position.Position_Target_IRR,
        Position_Realized_IRR: position.Position_Realized_IRR,
        Position_Total_Yield: position.Position_Total_Yield,
        Position_Cash_Yield: position.Position_Cash_Yield,
        Position_PIK: position.Position_PIK,
        Position_Fixed_Floating: position.Position_Fixed_Floating || '',
        Position_Fees_Upfront: position.Position_Fees_Upfront,
        Position_Date_Invested: parseDate(position.Position_Date_Invested),
        Position_Min_IRR_Guarantee: position.Position_Min_IRR_Guarantee,
        Position_Maturity: parseDate(position.Position_Maturity),
        Position_Total_Position_Size: position.Position_Total_Position_Size,
        Position_Amount_Committed: position.Position_Amount_Committed,
        Position_Amount_Invested: position.Position_Amount_Invested,
        Position_Currency: position.Position_Currency || '',
        Position_Annual_Ammortization: position.Position_Annual_Ammortization,
        Position_Opening_LTV: position.Position_Opening_LTV,
        Position_Opening_DEBT_EBITDA: position.Position_Opening_DEBT_EBITDA,
        Position_Date_Realized: parseDate(position.Position_Date_Realized),
        Position_Description_Loan: position.Position_Description_Loan || '',
        Position_Description_LTV: position.Position_Description_LTV || '',
        Position_Asof: parseDate(position.Position_Asof),
        Position_Deleted: position.Position_Deleted || false
      })
      setEditingId(position.Position_Id)

      // Reset identifier fields immediately before async load
      setPositionIdType('')
      setPositionIdValue('')
      setPositionIssuerIdType('')
      setPositionIssuerIdValue('')

      // Load existing identifiers for this position
      try {
        const token = await getAccessToken()
        if (token && position.Position_Name) {
          const loadIds = async (entityType: string) => {
            const resp = await fetch(
              `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/by-entity?entity_type=${encodeURIComponent(entityType)}&entity_name=${encodeURIComponent(position.Position_Name)}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            )
            if (resp.ok) return await resp.json()
            return []
          }
          const [posIds, posIssuerIds] = await Promise.all([loadIds('PositionID'), loadIds('PositionIssuerID')])
          if (posIds.length > 0) {
            setPositionIdType(posIds[0].Identifier_Name || '')
            setPositionIdValue(posIds[0].Identifier_Value || '')
          } else {
            setPositionIdType('')
            setPositionIdValue('')
          }
          if (posIssuerIds.length > 0) {
            setPositionIssuerIdType(posIssuerIds[0].Identifier_Name || '')
            setPositionIssuerIdValue(posIssuerIds[0].Identifier_Value || '')
          } else {
            setPositionIssuerIdType('')
            setPositionIssuerIdValue('')
          }
        }
      } catch (idErr) {
        console.error('Failed to load identifiers:', idErr)
      }

      // Scroll to the form section to make it visible
      const formElement = document.querySelector('[data-form-section]')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (error) {
      console.error('Error in handleEdit:', error)
      setError('Failed to load position data for editing')
    }
  }

  // Handle copy as new (create new historical entry with position data)
  const handleCopyAsNew = async (position: Position) => {
    try {
      // Convert date strings to Date objects if they're strings
      const parseDate = (dateValue: any): Date | null => {
        if (!dateValue) return null
        if (dateValue instanceof Date) return dateValue
        if (typeof dateValue === 'string') {
          const parsed = new Date(dateValue)
          return isNaN(parsed.getTime()) ? null : parsed
        }
        return null
      }

      setFormData({
        Position_Fund: position.Position_Fund || '',
        Position_Issuer: position.Position_Issuer || '',
        Position_Name: position.Position_Name || '',
        Position_Industry: position.Position_Industry || '',
        Position_Geography: position.Position_Geography || '',
        Position_Collateral: position.Position_Collateral || '',
        Position_Rating: position.Position_Rating || '',
        Position_Position_Type: position.Position_Position_Type || '',
        Position_Lien: position.Position_Lien || '',
        Position_Ownership: position.Position_Ownership || '',
        Position_Target_IRR: position.Position_Target_IRR,
        Position_Realized_IRR: position.Position_Realized_IRR,
        Position_Total_Yield: position.Position_Total_Yield,
        Position_Cash_Yield: position.Position_Cash_Yield,
        Position_PIK: position.Position_PIK,
        Position_Fixed_Floating: position.Position_Fixed_Floating || '',
        Position_Fees_Upfront: position.Position_Fees_Upfront,
        Position_Date_Invested: parseDate(position.Position_Date_Invested),
        Position_Min_IRR_Guarantee: position.Position_Min_IRR_Guarantee,
        Position_Maturity: parseDate(position.Position_Maturity),
        Position_Total_Position_Size: position.Position_Total_Position_Size,
        Position_Amount_Committed: position.Position_Amount_Committed,
        Position_Amount_Invested: position.Position_Amount_Invested,
        Position_Currency: position.Position_Currency || '',
        Position_Annual_Ammortization: position.Position_Annual_Ammortization,
        Position_Opening_LTV: position.Position_Opening_LTV,
        Position_Opening_DEBT_EBITDA: position.Position_Opening_DEBT_EBITDA,
        Position_Date_Realized: parseDate(position.Position_Date_Realized),
        Position_Description_Loan: position.Position_Description_Loan || '',
        Position_Description_LTV: position.Position_Description_LTV || '',
        Position_Asof: new Date(), // Set to today for new historical entry
        Position_Deleted: false
      })
      setEditingId(null) // Clear editing ID to indicate this is a new entry

      // Reset identifier fields immediately before async load
      setPositionIdType('')
      setPositionIdValue('')
      setPositionIssuerIdType('')
      setPositionIssuerIdValue('')

      // Load existing identifiers for this position (same as handleEdit)
      try {
        const token = await getAccessToken()
        if (token && position.Position_Name) {
          const loadIds = async (entityType: string) => {
            const resp = await fetch(
              `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/by-entity?entity_type=${encodeURIComponent(entityType)}&entity_name=${encodeURIComponent(position.Position_Name)}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            )
            if (resp.ok) return await resp.json()
            return []
          }
          const [posIds, posIssuerIds] = await Promise.all([loadIds('PositionID'), loadIds('PositionIssuerID')])
          if (posIds.length > 0) {
            setPositionIdType(posIds[0].Identifier_Name || '')
            setPositionIdValue(posIds[0].Identifier_Value || '')
          }
          if (posIssuerIds.length > 0) {
            setPositionIssuerIdType(posIssuerIds[0].Identifier_Name || '')
            setPositionIssuerIdValue(posIssuerIds[0].Identifier_Value || '')
          }
        }
      } catch (idErr) {
        console.error('Failed to load identifiers:', idErr)
      }

      setSuccess(`Form pre-filled with data from "${position.Position_Name}". Update As Of date if needed, then click Submit to create new entry.`)

      // Scroll to the form section to make it visible
      const formElement = document.querySelector('[data-form-section]')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (error) {
      console.error('Error in handleCopyAsNew:', error)
      setError('Failed to load position data for copying')
    }
  }

  // Handle delete
  const handleDelete = async () => {
    if (!positionToDelete) return
    
    setLoading(true)
    setError(null)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/positions/${positionToDelete.Position_Id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to delete position')
      }
      
      setSuccess('Position deleted successfully')
      setDeleteDialogOpen(false)
      setPositionToDelete(null)
      refreshPositions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setLoading(false)
    }
  }

  // Handle clear form
  const handleClear = () => {
    setFormData(initialPosition)
    setAppliedFilters(initialPosition)
    setEditingId(null)
    setPositionIdType('')
    setPositionIdValue('')
    setPositionIssuerIdType('')
    setPositionIssuerIdValue('')
  }

  // Handle search button click
  const handleSearch = () => {
    setAppliedFilters({ ...formData })
    setPage(0) // Reset pagination when filtering
  }

  // Check if any filter criteria are set in form
  const hasFilterCriteria = formData.Position_Fund || formData.Position_Issuer || formData.Position_Name || 
    formData.Position_Industry || formData.Position_Geography || formData.Position_Collateral ||
    formData.Position_Rating || formData.Position_Position_Type || formData.Position_Lien ||
    formData.Position_Ownership || formData.Position_Target_IRR !== null || formData.Position_Realized_IRR !== null ||
    formData.Position_Total_Yield !== null || formData.Position_Cash_Yield !== null || formData.Position_PIK !== null ||
    formData.Position_Fixed_Floating || formData.Position_Fees_Upfront !== null || formData.Position_Date_Invested ||
    formData.Position_Min_IRR_Guarantee !== null || formData.Position_Maturity || formData.Position_Total_Position_Size !== null ||
    formData.Position_Amount_Committed !== null || formData.Position_Amount_Invested !== null || formData.Position_Currency ||
    formData.Position_Annual_Ammortization !== null || formData.Position_Opening_LTV !== null || 
    formData.Position_Opening_DEBT_EBITDA !== null || formData.Position_Date_Realized || 
    formData.Position_Description_Loan || formData.Position_Description_LTV || formData.Position_Asof

  // Check if any filters are currently applied
  const hasAppliedFilters = appliedFilters.Position_Fund || appliedFilters.Position_Issuer || appliedFilters.Position_Name || 
    appliedFilters.Position_Industry || appliedFilters.Position_Geography || appliedFilters.Position_Collateral ||
    appliedFilters.Position_Rating || appliedFilters.Position_Position_Type || appliedFilters.Position_Lien ||
    appliedFilters.Position_Ownership || appliedFilters.Position_Target_IRR !== null || appliedFilters.Position_Realized_IRR !== null ||
    appliedFilters.Position_Total_Yield !== null || appliedFilters.Position_Cash_Yield !== null || appliedFilters.Position_PIK !== null ||
    appliedFilters.Position_Fixed_Floating || appliedFilters.Position_Fees_Upfront !== null || appliedFilters.Position_Date_Invested ||
    appliedFilters.Position_Min_IRR_Guarantee !== null || appliedFilters.Position_Maturity || appliedFilters.Position_Total_Position_Size !== null ||
    appliedFilters.Position_Amount_Committed !== null || appliedFilters.Position_Amount_Invested !== null || appliedFilters.Position_Currency ||
    appliedFilters.Position_Annual_Ammortization !== null || appliedFilters.Position_Opening_LTV !== null || 
    appliedFilters.Position_Opening_DEBT_EBITDA !== null || appliedFilters.Position_Date_Realized || 
    appliedFilters.Position_Description_Loan || appliedFilters.Position_Description_LTV || appliedFilters.Position_Asof

  // Filter positions
  const filteredPositions = positions.filter(position => {
    // Apply form-based filters only if they have been applied via search button
    const matchesFormFilters = !hasAppliedFilters || (
      (!appliedFilters.Position_Fund || position.Position_Fund.toLowerCase().includes(appliedFilters.Position_Fund.toLowerCase())) &&
      (!appliedFilters.Position_Issuer || position.Position_Issuer.toLowerCase().includes(appliedFilters.Position_Issuer.toLowerCase())) &&
      (!appliedFilters.Position_Name || position.Position_Name.toLowerCase().includes(appliedFilters.Position_Name.toLowerCase())) &&
      (!appliedFilters.Position_Industry || position.Position_Industry === appliedFilters.Position_Industry) &&
      (!appliedFilters.Position_Geography || position.Position_Geography === appliedFilters.Position_Geography) &&
      (!appliedFilters.Position_Collateral || position.Position_Collateral.toLowerCase().includes(appliedFilters.Position_Collateral.toLowerCase())) &&
      (!appliedFilters.Position_Rating || position.Position_Rating === appliedFilters.Position_Rating) &&
      (!appliedFilters.Position_Position_Type || position.Position_Position_Type === appliedFilters.Position_Position_Type) &&
      (!appliedFilters.Position_Lien || position.Position_Lien === appliedFilters.Position_Lien) &&
      (!appliedFilters.Position_Ownership || position.Position_Ownership.toLowerCase().includes(appliedFilters.Position_Ownership.toLowerCase())) &&
      (appliedFilters.Position_Target_IRR === null || position.Position_Target_IRR === appliedFilters.Position_Target_IRR) &&
      (appliedFilters.Position_Realized_IRR === null || position.Position_Realized_IRR === appliedFilters.Position_Realized_IRR) &&
      (appliedFilters.Position_Total_Yield === null || position.Position_Total_Yield === appliedFilters.Position_Total_Yield) &&
      (appliedFilters.Position_Cash_Yield === null || position.Position_Cash_Yield === appliedFilters.Position_Cash_Yield) &&
      (appliedFilters.Position_PIK === null || position.Position_PIK === appliedFilters.Position_PIK) &&
      (!appliedFilters.Position_Fixed_Floating || position.Position_Fixed_Floating === appliedFilters.Position_Fixed_Floating) &&
      (appliedFilters.Position_Fees_Upfront === null || position.Position_Fees_Upfront === appliedFilters.Position_Fees_Upfront) &&
      (!appliedFilters.Position_Date_Invested || (position.Position_Date_Invested && position.Position_Date_Invested.toDateString() === appliedFilters.Position_Date_Invested.toDateString())) &&
      (appliedFilters.Position_Min_IRR_Guarantee === null || position.Position_Min_IRR_Guarantee === appliedFilters.Position_Min_IRR_Guarantee) &&
      (!appliedFilters.Position_Maturity || (position.Position_Maturity && position.Position_Maturity.toDateString() === appliedFilters.Position_Maturity.toDateString())) &&
      (appliedFilters.Position_Total_Position_Size === null || position.Position_Total_Position_Size === appliedFilters.Position_Total_Position_Size) &&
      (appliedFilters.Position_Amount_Committed === null || position.Position_Amount_Committed === appliedFilters.Position_Amount_Committed) &&
      (appliedFilters.Position_Amount_Invested === null || position.Position_Amount_Invested === appliedFilters.Position_Amount_Invested) &&
      (!appliedFilters.Position_Currency || position.Position_Currency === appliedFilters.Position_Currency) &&
      (appliedFilters.Position_Annual_Ammortization === null || position.Position_Annual_Ammortization === appliedFilters.Position_Annual_Ammortization) &&
      (appliedFilters.Position_Opening_LTV === null || position.Position_Opening_LTV === appliedFilters.Position_Opening_LTV) &&
      (appliedFilters.Position_Opening_DEBT_EBITDA === null || position.Position_Opening_DEBT_EBITDA === appliedFilters.Position_Opening_DEBT_EBITDA) &&
      (!appliedFilters.Position_Date_Realized || (position.Position_Date_Realized && position.Position_Date_Realized.toDateString() === appliedFilters.Position_Date_Realized.toDateString())) &&
      (!appliedFilters.Position_Description_Loan || position.Position_Description_Loan.toLowerCase().includes(appliedFilters.Position_Description_Loan.toLowerCase())) &&
      (!appliedFilters.Position_Description_LTV || position.Position_Description_LTV.toLowerCase().includes(appliedFilters.Position_Description_LTV.toLowerCase())) &&
      (!appliedFilters.Position_Asof || (position.Position_Asof && position.Position_Asof.toDateString() === appliedFilters.Position_Asof.toDateString()))
    )
    
    return matchesFormFilters
  })

  // Apply sorting to filtered positions
  const { sortedData: sortedPositions, handleSort, getSortDirection, getSortIndex } = useTableSort({
    data: filteredPositions,
    initialSort: [{ field: 'Position_Name', direction: 'asc' }]
  })

  // Paginated positions
  const paginatedPositions = sortedPositions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
          Positions Management
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }} data-form-section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">
                  {editingId ? 'Edit Position' : hasFilterCriteria ? 'Add New Position / Search Filters' : 'Add New Position'}
                </Typography>
              </Box>
              {hasFilterCriteria && !editingId && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                  <Typography variant="body2">
                    Form fields are being used to filter the positions list. Click "Search" to apply filters or "Clear" to reset.
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Position Name *"
                    value={formData.Position_Name}
                    onChange={(e) => handleInputChange('Position_Name', e.target.value)}
                    fullWidth
                    required
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fund</InputLabel>
                    <Select
                      value={formData.Position_Fund}
                      onChange={(e) => handleInputChange('Position_Fund', e.target.value)}
                      label="Fund"
                    >
                      {funds.map(fund => (
                        <MenuItem key={fund} value={fund}>
                          {fund}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Issuer"
                    value={formData.Position_Issuer}
                    onChange={(e) => handleInputChange('Position_Issuer', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Industry</InputLabel>
                    <Select
                      value={formData.Position_Industry}
                      onChange={(e) => handleInputChange('Position_Industry', e.target.value)}
                      label="Industry"
                    >
                      {industries.map(industry => (
                        <MenuItem key={industry} value={industry}>
                          {industry}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Geography</InputLabel>
                    <Select
                      value={formData.Position_Geography}
                      onChange={(e) => handleInputChange('Position_Geography', e.target.value)}
                      label="Geography"
                    >
                      {geographies.map(geography => (
                        <MenuItem key={geography} value={geography}>
                          {geography}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Collateral"
                    value={formData.Position_Collateral}
                    onChange={(e) => handleInputChange('Position_Collateral', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Rating</InputLabel>
                    <Select
                      value={formData.Position_Rating}
                      onChange={(e) => handleInputChange('Position_Rating', e.target.value)}
                      label="Rating"
                    >
                      {ratings.map(rating => (
                        <MenuItem key={rating} value={rating}>
                          {rating}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Position Type</InputLabel>
                    <Select
                      value={formData.Position_Position_Type}
                      onChange={(e) => handleInputChange('Position_Position_Type', e.target.value)}
                      label="Position Type"
                    >
                      {positionTypes.map(type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Lien</InputLabel>
                    <Select
                      value={formData.Position_Lien}
                      onChange={(e) => handleInputChange('Position_Lien', e.target.value)}
                      label="Lien"
                    >
                      {lienTypes.map(lien => (
                        <MenuItem key={lien} value={lien}>
                          {lien}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Ownership"
                    value={formData.Position_Ownership}
                    onChange={(e) => handleInputChange('Position_Ownership', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g., 15%"
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Target IRR (%)"
                    type="number"
                    value={formData.Position_Target_IRR || ''}
                    onChange={(e) => handleInputChange('Position_Target_IRR', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Realized IRR (%)"
                    type="number"
                    value={formData.Position_Realized_IRR !== null && formData.Position_Realized_IRR !== undefined ? formData.Position_Realized_IRR : ''}
                    onChange={(e) => handleInputChange('Position_Realized_IRR', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Total Yield (%)"
                    type="number"
                    value={formData.Position_Total_Yield || ''}
                    onChange={(e) => handleInputChange('Position_Total_Yield', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Cash Yield (%)"
                    type="number"
                    value={formData.Position_Cash_Yield || ''}
                    onChange={(e) => handleInputChange('Position_Cash_Yield', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="PIK (%)"
                    type="number"
                    value={formData.Position_PIK || ''}
                    onChange={(e) => handleInputChange('Position_PIK', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fixed/Floating</InputLabel>
                    <Select
                      value={formData.Position_Fixed_Floating}
                      onChange={(e) => handleInputChange('Position_Fixed_Floating', e.target.value)}
                      label="Fixed/Floating"
                    >
                      {fixedFloating.map(type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Upfront Fees (%)"
                    type="number"
                    value={formData.Position_Fees_Upfront || ''}
                    onChange={(e) => handleInputChange('Position_Fees_Upfront', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <DatePicker
                    label="Date Invested"
                    value={formData.Position_Date_Invested}
                    onChange={(date) => handleInputChange('Position_Date_Invested', date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Min IRR Guarantee (%)"
                    type="number"
                    value={formData.Position_Min_IRR_Guarantee || ''}
                    onChange={(e) => handleInputChange('Position_Min_IRR_Guarantee', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <DatePicker
                    label="Maturity Date"
                    value={formData.Position_Maturity}
                    onChange={(date) => handleInputChange('Position_Maturity', date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Total Position Size"
                    type="number"
                    value={formData.Position_Total_Position_Size || ''}
                    onChange={(e) => handleInputChange('Position_Total_Position_Size', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Amount Committed"
                    type="number"
                    value={formData.Position_Amount_Committed || ''}
                    onChange={(e) => handleInputChange('Position_Amount_Committed', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Amount Invested"
                    type="number"
                    value={formData.Position_Amount_Invested !== null && formData.Position_Amount_Invested !== undefined ? formData.Position_Amount_Invested : ''}
                    onChange={(e) => handleInputChange('Position_Amount_Invested', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={formData.Position_Currency}
                      onChange={(e) => handleInputChange('Position_Currency', e.target.value)}
                      label="Currency"
                    >
                      {currencies.map(currency => (
                        <MenuItem key={currency} value={currency}>
                          {currency}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Annual Amortization"
                    type="number"
                    value={formData.Position_Annual_Ammortization || ''}
                    onChange={(e) => handleInputChange('Position_Annual_Ammortization', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Opening LTV (%)"
                    type="number"
                    value={formData.Position_Opening_LTV || ''}
                    onChange={(e) => handleInputChange('Position_Opening_LTV', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.1, min: 0, max: 100 }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Opening Debt/EBITDA"
                    type="number"
                    value={formData.Position_Opening_DEBT_EBITDA || ''}
                    onChange={(e) => handleInputChange('Position_Opening_DEBT_EBITDA', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <DatePicker
                    label="Date Realized"
                    value={formData.Position_Date_Realized}
                    onChange={(date) => handleInputChange('Position_Date_Realized', date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="As Of Date"
                    value={formData.Position_Asof}
                    onChange={(date) => handleInputChange('Position_Asof', date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.Position_Deleted}
                        onChange={(e) => handleInputChange('Position_Deleted', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Deleted"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Loan Description"
                    value={formData.Position_Description_Loan}
                    onChange={(e) => handleInputChange('Position_Description_Loan', e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="LTV Description"
                    value={formData.Position_Description_LTV}
                    onChange={(e) => handleInputChange('Position_Description_LTV', e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                  />
                </Grid>

                {/* Identifiers Section */}
                <Grid item xs={12}>
                  <Accordion defaultExpanded={false} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }}>
                      <Typography variant="body2" fontWeight={500}>Identifiers</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 1 }}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Position Identifier</InputLabel>
                            <Select
                              value={positionIdType}
                              onChange={(e) => setPositionIdType(e.target.value)}
                              label="Position Identifier"
                            >
                              <MenuItem value="">None</MenuItem>
                              {IDENTIFIER_TYPES.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Position Identifier Value"
                            value={positionIdValue}
                            onChange={(e) => setPositionIdValue(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={!positionIdType}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Position Issuer Identifier</InputLabel>
                            <Select
                              value={positionIssuerIdType}
                              onChange={(e) => setPositionIssuerIdType(e.target.value)}
                              label="Position Issuer Identifier"
                            >
                              <MenuItem value="">None</MenuItem>
                              {IDENTIFIER_TYPES.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Position Issuer Identifier Value"
                            value={positionIssuerIdValue}
                            onChange={(e) => setPositionIssuerIdValue(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={!positionIssuerIdType}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSubmit}
                    disabled={loading || !formData.Position_Name}
                    sx={{ flex: hasFilterCriteria && !editingId ? 1 : 2 }}
                  >
                    {loading ? 'Processing...' : (editingId ? 'Update' : 'Submit')}
                  </Button>
                  {hasFilterCriteria && !editingId && (
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      disabled={loading}
                      sx={{ flex: 1 }}
                    >
                      Search
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClear}
                    disabled={loading}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CalculateIcon />}
                    onClick={calculateMetrics}
                    disabled={loading}
                    title="Calculate ownership % and total yield"
                  >
                    Calculate
                  </Button>
                </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Table Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="h6">
                    Positions List ({filteredPositions.length} of {totalCount})
                  </Typography>
                  {hasAppliedFilters && (
                    <Typography variant="body2" color="text.secondary">
                      Filtered by form criteria
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <IconButton onClick={refreshPositions} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableHeader
                        field="Position_Name"
                        label="Position Name"
                        sortDirection={getSortDirection('Position_Name')}
                        sortIndex={getSortIndex('Position_Name')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Position_Fund"
                        label="Fund"
                        sortDirection={getSortDirection('Position_Fund')}
                        sortIndex={getSortIndex('Position_Fund')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Position_Industry"
                        label="Industry"
                        sortDirection={getSortDirection('Position_Industry')}
                        sortIndex={getSortIndex('Position_Industry')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Position_Position_Type"
                        label="Type"
                        sortDirection={getSortDirection('Position_Position_Type')}
                        sortIndex={getSortIndex('Position_Position_Type')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Position_Rating"
                        label="Rating"
                        sortDirection={getSortDirection('Position_Rating')}
                        sortIndex={getSortIndex('Position_Rating')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Position_Amount_Invested"
                        label="Amount Invested"
                        sortDirection={getSortDirection('Position_Amount_Invested')}
                        sortIndex={getSortIndex('Position_Amount_Invested')}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortableTableHeader
                        field="Position_Asof"
                        label="As Of Date"
                        sortDirection={getSortDirection('Position_Asof')}
                        sortIndex={getSortIndex('Position_Asof')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Position_Timestamp"
                        label="Timestamp"
                        sortDirection={getSortDirection('Position_Timestamp')}
                        sortIndex={getSortIndex('Position_Timestamp')}
                        onSort={handleSort}
                      />
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && positions.length === 0 ? (
                      // Show skeleton rows while loading
                      Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Skeleton variant="circular" width={24} height={24} />
                              <Skeleton variant="circular" width={24} height={24} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : paginatedPositions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No positions found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedPositions.map((position) => (
                        <TableRow key={position.Position_Id}>
                          <TableCell>{position.Position_Name}</TableCell>
                          <TableCell>{position.Position_Fund}</TableCell>
                          <TableCell>{position.Position_Industry}</TableCell>
                          <TableCell>{position.Position_Position_Type}</TableCell>
                          <TableCell>{position.Position_Rating}</TableCell>
                          <TableCell align="right">
                            {position.Position_Amount_Invested !== null && position.Position_Amount_Invested !== undefined ? position.Position_Amount_Invested.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell>
                            {position.Position_Asof ? new Date(position.Position_Asof).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            }) : '-'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {position.Position_Timestamp ? new Date(position.Position_Timestamp).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Copy as New Entry">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyAsNew(position)}
                                  disabled={loading}
                                  color="primary"
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit This Entry">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(position)}
                                  disabled={loading}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setPositionToDelete(position)
                                    setDeleteDialogOpen(true)
                                  }}
                                  disabled={loading}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredPositions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10))
                  setPage(0)
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the position "{positionToDelete?.Position_Name}"?
              This action will mark the position as deleted but preserve the data for audit purposes.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" disabled={loading}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default PositionsManagement